import React, { useEffect, useState } from 'react';
import { Modal, Button, TextField, Select, MenuItem, FormControl, InputLabel, Chip, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { Add } from '@mui/icons-material';
import { Account, Segment, Transaction, TransactionCreate, TransactionPostingStatus } from '../../interfaces/accounting.interface';
import ApprovalStatusCard from '../approvals/ApprovalStatusCard';   // FW-3B
import { TransactionService } from '../../services/transaction.service';
import { AccountService } from '../../services/account.service';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import SearchableSelect from '../common/SearchableSelect';
import { SegmentService } from '../../services/segment.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import { leafAccounts } from '../../utils/postingAccounts';
import { CashbookAccessService } from '../../services/cashbookAccess.service';   // F11 (11b)
import { TxnMappingRuleService } from '../../services/txnMappingRules.service';   // F11 (11c)
import { CategorySuggestion } from '../../interfaces/cashbook/cashbook.interface';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 500,
    backgroundColor: '#fff',
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
    padding: '32px',
    borderRadius: '8px',
    maxHeight: '90vh',
    overflowY: 'auto' as const
};

// ---- FW-3B: where a cash transaction stands; only POSTED has a journal and counts in reports and budgets ----
const STATUS_LABEL: Record<TransactionPostingStatus, string> = {
    POSTED: 'Posted', PENDING_APPROVAL: 'Pending approval', APPROVED: 'Approved, not posted', REJECTED: 'Rejected',
};
const STATUS_COLOR: Record<TransactionPostingStatus, 'success' | 'warning' | 'info' | 'error'> = {
    POSTED: 'success', PENDING_APPROVAL: 'warning', APPROVED: 'info', REJECTED: 'error',
};
const STATUS_TIP: Record<TransactionPostingStatus, string> = {
    POSTED: 'Booked to the books',
    PENDING_APPROVAL: 'Held until it is approved: no journal entry yet, not counted in reports or budgets',
    APPROVED: 'Approved: press Post to book it to the books',
    REJECTED: 'Rejected: edit and resubmit it, or delete it',
};
// ---- end FW-3B ----

const TransactionList: React.FC = () => {
    const toast = useToast();
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [segments, setSegments] = useState<Segment[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Filter state
    const [filterType, setFilterType] = useState<string>('');
    const [filterStartDate, setFilterStartDate] = useState<string>('');
    const [filterEndDate, setFilterEndDate] = useState<string>('');
    const [filterStatus, setFilterStatus] = useState<string>('');   // FW-3B
    const [editingId, setEditingId] = useState<string | null>(null);   // FW-3B: a rejected cash-out being edited
    const [detailTxn, setDetailTxn] = useState<Transaction | null>(null);   // FW-3B: its approval card
    const [busyId, setBusyId] = useState<string | null>(null);
    const [notPostable, setNotPostable] = useState<string[]>([]);   // F11 (11b): restricted cashbooks this user may not post to
    const [suggestion, setSuggestion] = useState<CategorySuggestion | null>(null);   // F11 (11c): "Auto" preview

    // Form state
    const [formData, setFormData] = useState<{
        transactionType: 'INCOME' | 'EXPENSE';
        amount: number;
        date: string;
        categoryAccountId: string;
        paymentAccountId: string;
        description: string;
        reference: string;
        segmentId: string;
    }>({
        transactionType: 'INCOME',
        amount: 0,
        date: toLocalIsoDate(),
        categoryAccountId: '',
        paymentAccountId: '',
        description: '',
        reference: '',
        segmentId: ''
    });

    const fetchTransactions = async (pageNum: number) => {
        try {
            setLoading(true);
            const response = await TransactionService.getTransactionsByInstitution(
                pageNum,
                10,
                filterType || undefined,
                filterStartDate || undefined,
                filterEndDate || undefined,
                filterStatus || undefined
            );
            setTransactions(response.content || []);
            setTotalPages(response.totalPages || 0);
            setPage(response.number || 0);
        } catch (err) {
            toast.showApiError(err, 'Failed to load transactions');
        } finally {
            setLoading(false);
        }
    };

    const fetchAccounts = async () => {
        try {
            const data = await AccountService.getAllAccounts();
            setAccounts(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching accounts:', err);
        }
    };

    const fetchSegments = async () => {
        try {
            const response = await SegmentService.getByInstitution();
            const active: Segment[] = (response.content || []).filter((s: Segment) => s.active !== false);
            setSegments(active);
        } catch (err) {
            console.error('Error fetching segments:', err);
        }
    };

    // F11 (11b): hide the cashbooks this user may not post to from the payment picker (the server refuses them anyway).
    const fetchMyCashbooks = async () => {
        try {
            const mine = await CashbookAccessService.mine(AuthService.getSelectedInstitution());
            setNotPostable(mine?.notPostableAccountIds ?? []);
        } catch (err) {
            console.error('Error fetching cashbook access:', err);
        }
    };

    useEffect(() => {
        fetchTransactions(0);
        fetchAccounts();
        fetchSegments();
        fetchMyCashbooks();
    }, []);

    const handleFilter = () => {
        fetchTransactions(0);
    };

    // Group (header) accounts only total their sub-accounts; a transaction posts to leaves only.
    const postingAccounts = leafAccounts(accounts);
    const revenueAccounts = postingAccounts.filter(a => a.accountType === 'REVENUE');
    const expenseAccounts = postingAccounts.filter(a => a.accountType === 'EXPENSE');
    const assetAccounts = postingAccounts.filter(a => a.accountType === 'ASSET' && !notPostable.includes(a.id!));

    const getCategoryAccounts = () => {
        return formData.transactionType === 'INCOME' ? revenueAccounts : expenseAccounts;
    };

    const handleOpenModal = () => {
        setEditingId(null);
        setSuggestion(null);
        setFormData({
            transactionType: 'INCOME',
            amount: 0,
            date: toLocalIsoDate(),
            categoryAccountId: '',
            paymentAccountId: '',
            description: '',
            reference: '',
            segmentId: ''
        });
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingId(null);
    };

    // ---- FW-3B: post an approved cash-out, edit and resubmit a rejected one, delete a held one ----
    const runAction = async (id: string, work: () => Promise<unknown>, done: string, failed: string) => {
        setBusyId(id);
        try {
            await work();
            toast.showSuccess(done);
            await fetchTransactions(page);
        } catch (err) {
            toast.showApiError(err, failed);
        } finally {
            setBusyId(null);
        }
    };
    const handlePost = (txn: Transaction) => runAction(txn.id!, () => TransactionService.postTransaction(txn.id!), 'Transaction posted', 'Failed to post the transaction');
    const handleDelete = (txn: Transaction) => {
        if (!window.confirm('Delete this held transaction? Any approval request for it is closed.')) return;
        return runAction(txn.id!, () => TransactionService.discardHeldTransaction(txn.id!), 'Transaction deleted', 'Failed to delete the transaction');
    };
    const handleEdit = (txn: Transaction) => {
        setEditingId(txn.id!);
        setSuggestion(null);
        setFormData({
            transactionType: txn.transactionType === 'INCOME' ? 'INCOME' : 'EXPENSE',   // F11d: a held cash-in keeps its side
            amount: txn.amount,
            date: txn.date,
            categoryAccountId: txn.categoryAccountId,
            paymentAccountId: txn.paymentAccountId,
            description: txn.description || '',
            reference: txn.reference || '',
            segmentId: txn.segmentId || ''
        });
        setIsModalOpen(true);
    };
    // ---- end FW-3B ----

    // F11 (11c): show which mapping rule would set the category when it is left on "Auto".
    const handleSuggest = async () => {
        try {
            const res = await TxnMappingRuleService.suggest(AuthService.getSelectedInstitution(), {
                transactionType: formData.transactionType,
                paymentAccountId: formData.paymentAccountId || undefined,
                amount: Number(formData.amount) || undefined,
                description: formData.description || undefined,
                reference: formData.reference || undefined,
            });
            setSuggestion(res);
        } catch (err) {
            toast.showApiError(err, 'Failed to check the mapping rules');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const institutionId = AuthService.getSelectedInstitution();
            const transactionData: TransactionCreate = {
                transactionType: formData.transactionType,
                amount: Number(formData.amount),
                date: formData.date,
                categoryAccountId: formData.categoryAccountId || undefined,   // F11 (11c): blank = Auto
                paymentAccountId: formData.paymentAccountId,
                description: formData.description || undefined,
                reference: formData.reference || undefined,
                institutionId,
                segmentId: formData.segmentId || undefined
            };
            if (editingId) {   // FW-3B
                await TransactionService.editRejectedTransaction(editingId, transactionData);
                toast.showSuccess('Transaction updated');
            } else {
                const created = await TransactionService.createTransaction(transactionData);
                if (created?.postingStatus && created.postingStatus !== 'POSTED') {
                    toast.showSuccess('Held for approval: it will post after it is approved');   // FW-3B
                }
            }
            await fetchTransactions(page);
            handleCloseModal();
        } catch (err) {
            toast.showApiError(err, editingId ? 'Failed to update the transaction' : 'Failed to create transaction');
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div style={{ padding: '20px' }} data-help-page="accounting.transactionList.page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' , flexWrap: 'wrap', gap: '8px' }}>
                <h2>Transactions</h2>
                <Button data-help="accounting.transactionList.record" variant="contained" color="primary" startIcon={<Add />} onClick={handleOpenModal}>
                    Record Transaction
                </Button>
            </div>

            {/* Filter Bar */}
            <div data-help-section="accounting.transactionList.filters" style={{ display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl data-help="accounting.transactionList.type" size="small" sx={{ flex: '1 1 150px', maxWidth: 220 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                        value={filterType}
                        label="Type"
                        onChange={(e) => setFilterType(e.target.value)}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="INCOME">INCOME</MenuItem>
                        <MenuItem value="EXPENSE">EXPENSE</MenuItem>
                    </Select>
                </FormControl>
                {/* FW-3B: status filter */}
                <FormControl data-help="accounting.transactionList.statusFilter" size="small" sx={{ flex: '1 1 170px', maxWidth: 240 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={filterStatus}
                        label="Status"
                        inputProps={{ 'aria-label': 'Status' }}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        <MenuItem value="">All</MenuItem>
                        {(Object.keys(STATUS_LABEL) as TransactionPostingStatus[]).map(k => (
                            <MenuItem key={k} value={k}>{STATUS_LABEL[k]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField data-help="accounting.transactionList.from"
                    size="small"
                    label="Start Date"
                    type="date"
                    sx={{ flex: '1 1 150px', maxWidth: 220 }}
                    value={filterStartDate}
                    onChange={(e) => setFilterStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField data-help="accounting.transactionList.to"
                    size="small"
                    label="End Date"
                    type="date"
                    sx={{ flex: '1 1 150px', maxWidth: 220 }}
                    value={filterEndDate}
                    onChange={(e) => setFilterEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
                <Button data-help="accounting.transactionList.filter" variant="outlined" onClick={handleFilter}>
                    Filter
                </Button>
            </div>

            <div data-help-section="accounting.transactionList.table">
            <ScrollableTable>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead>
                    <tr>
                        <th data-help="accounting.transactionList.col.date" style={tableHeaderStyle}>Date</th>
                        <th data-help="accounting.transactionList.col.type" style={tableHeaderStyle}>Type</th>
                        <th data-help="accounting.transactionList.col.category" style={tableHeaderStyle}>Category</th>
                        <th data-help="accounting.transactionList.col.amount" style={tableHeaderStyle}>Amount</th>
                        <th data-help="accounting.transactionList.col.description" style={tableHeaderStyle}>Description</th>
                        <th data-help="accounting.transactionList.col.reference" style={tableHeaderStyle}>Reference</th>
                        <th data-help="accounting.transactionList.col.status" style={tableHeaderStyle}>Status</th>
                        <th data-help="accounting.transactionList.col.actions" style={tableHeaderStyle}></th>
                    </tr>
                </thead>
                <tbody>
                    {transactions.length === 0 ? (
                        <tr>
                            <td style={tableCellStyle} colSpan={8}>No transactions found</td>
                        </tr>
                    ) : (
                        transactions.map(txn => (
                            <tr key={txn.id}>
                                <td style={tableCellStyle}>{txn.date ? formatDate(txn.date) : '-'}</td>
                                <td style={tableCellStyle}>{txn.transactionType}</td>
                                <td style={tableCellStyle}>{txn.categoryAccountName || '-'}</td>
                                <td style={{
                                    ...tableCellStyle,
                                    color: txn.transactionType === 'INCOME' ? 'green' : 'red',
                                    fontWeight: 'bold'
                                }}>
                                    {txn.transactionType === 'INCOME' ? '+' : '-'}{txn.amount.toFixed(2)}
                                </td>
                                <td style={tableCellStyle}>{txn.description || '-'}</td>
                                <td style={tableCellStyle}>{txn.reference || '-'}</td>
                                {/* ---- FW-3B ---- */}
                                <td style={tableCellStyle}>
                                    {(() => {
                                        const st: TransactionPostingStatus = txn.postingStatus ?? 'POSTED';
                                        return (
                                            <Tooltip title={STATUS_TIP[st]}>
                                                <Chip data-help="accounting.transactionList.status" size="small" color={STATUS_COLOR[st]} label={STATUS_LABEL[st]} data-testid={`txn-status-${txn.id}`} />
                                            </Tooltip>
                                        );
                                    })()}
                                </td>
                                <td style={{ ...tableCellStyle, whiteSpace: 'nowrap' }}>
                                    {txn.postingStatus === 'APPROVED' && (
                                        <Button data-help="accounting.transactionList.post" size="small" variant="contained" disabled={busyId === txn.id} onClick={() => handlePost(txn)}>Post</Button>
                                    )}
                                    {txn.postingStatus === 'REJECTED' && (
                                        <Button data-help="accounting.transactionList.resubmit" size="small" disabled={busyId === txn.id} onClick={() => handleEdit(txn)}>Edit and resubmit</Button>
                                    )}
                                    {txn.postingStatus && txn.postingStatus !== 'POSTED' && (
                                        <>
                                            <Button data-help="accounting.transactionList.action.approval" size="small" onClick={() => setDetailTxn(txn)}>Approval</Button>
                                            <Button data-help="accounting.transactionList.action.delete" size="small" color="error" disabled={busyId === txn.id} onClick={() => handleDelete(txn)}>Delete</Button>
                                        </>
                                    )}
                                </td>
                                {/* ---- end FW-3B ---- */}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
            </ScrollableTable>
            </div>

            {/* Pagination */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '20px' }}>
                <Button data-help="accounting.transactionList.prev" variant="outlined" disabled={page === 0} onClick={() => fetchTransactions(page - 1)}>
                    Previous
                </Button>
                <span>Page {page + 1} of {totalPages || 1}</span>
                <Button data-help="accounting.transactionList.next" variant="outlined" disabled={page >= totalPages - 1} onClick={() => fetchTransactions(page + 1)}>
                    Next
                </Button>
            </div>

            {/* Record Transaction Modal */}
            <Modal open={isModalOpen} onClose={handleCloseModal}>
                <div data-help-page="accounting.transactionList.form.page" style={modalStyle}>
                    <h2>{editingId ? 'Edit and resubmit' : 'Record Transaction'}</h2>
                    <form onSubmit={handleSubmit}>
                        <FormControl data-help="accounting.transactionList.form.field.type" fullWidth margin="normal" required>
                            <InputLabel>Transaction Type</InputLabel>
                            <Select
                                value={formData.transactionType}
                                disabled={!!editingId}
                                label="Transaction Type"
                                onChange={(e) => {
                                    setFormData({
                                        ...formData,
                                        transactionType: e.target.value as 'INCOME' | 'EXPENSE',
                                        categoryAccountId: ''
                                    });
                                    setSuggestion(null);
                                }}
                            >
                                <MenuItem value="INCOME">INCOME</MenuItem>
                                <MenuItem value="EXPENSE">EXPENSE</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField data-help="accounting.transactionList.form.field.amount"
                            fullWidth
                            label="Amount"
                            type="number"
                            value={formData.amount}
                            onChange={(e) => setFormData({ ...formData, amount: Number(e.target.value) })}
                            required
                            margin="normal"
                            inputProps={{ min: 0, step: '0.01' }}
                        />
                        <TextField data-help="accounting.transactionList.form.field.date"
                            fullWidth
                            label="Date"
                            type="date"
                            value={formData.date}
                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                            required
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                        />
                        <AccountAutocomplete data-help="accounting.transactionList.form.field.category"
                            accounts={getCategoryAccounts()}
                            value={formData.categoryAccountId}
                            onChange={(id) => { setFormData({ ...formData, categoryAccountId: id }); setSuggestion(null); }}
                            label="Category Account"
                            helperText="Leave blank for Auto: the first matching transaction mapping rule sets it"
                        />
                        {/* ---- F11 (11c): Auto preview ---- */}
                        {!formData.categoryAccountId && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                                <Button data-help="accounting.transactionList.action.checkRule" size="small" onClick={handleSuggest}>Check mapping rule</Button>
                                {suggestion && (
                                    <span data-testid="txn-suggestion" style={{ fontSize: 13 }}>
                                        {suggestion.matched
                                            ? `Rule "${suggestion.ruleName}" sets ${suggestion.categoryCode ?? ''} ${suggestion.categoryName ?? ''}`.trim()
                                            : 'No rule matches: choose a category account'}
                                    </span>
                                )}
                            </div>
                        )}
                        {/* ---- end F11 ---- */}
                        <AccountAutocomplete data-help="accounting.transactionList.form.field.payment"
                            accounts={assetAccounts}
                            value={formData.paymentAccountId}
                            onChange={(id) => setFormData({ ...formData, paymentAccountId: id })}
                            label="Payment Account"
                            required
                        />
                        <TextField data-help="accounting.transactionList.form.field.description"
                            fullWidth
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            margin="normal"
                            multiline
                            rows={2}
                        />
                        <TextField data-help="accounting.transactionList.form.field.reference"
                            fullWidth
                            label="Reference"
                            value={formData.reference}
                            onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                            margin="normal"
                        />
                        <SearchableSelect data-help="accounting.transactionList.form.field.segment"
                            options={segments}
                            value={formData.segmentId}
                            onChange={(id) => setFormData({ ...formData, segmentId: id })}
                            getOptionLabel={(s) => `${s.code} — ${s.name}`}
                            label="Segment"
                            placeholder="(default)"
                            sx={{ mt: 2, mb: 1 }}
                        />
                        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                            <Button data-help="accounting.transactionList.form.cancel" onClick={handleCloseModal}>Cancel</Button>
                            <Button data-help="accounting.transactionList.form.save" type="submit" variant="contained" color="primary">
                                Record
                            </Button>
                        </div>
                    </form>
                </div>
            </Modal>

            {/* ---- FW-3B: the approval card of a held cash-out ---- */}
            <Dialog data-help-page="accounting.transactionList.approval.page" open={!!detailTxn} onClose={() => setDetailTxn(null)} fullWidth maxWidth="sm">
                <DialogTitle>{detailTxn?.transactionType === 'INCOME' ? 'Cash-in approval' : 'Cash-out approval'}</DialogTitle>
                <DialogContent>
                    {detailTxn && (
                        <>
                            <p>{detailTxn.categoryAccountName} - {detailTxn.amount.toFixed(2)}</p>
                            <ApprovalStatusCard subjectType={detailTxn.transactionType === 'INCOME' ? 'CASH_IN' : 'CASH_OUT'} subjectId={detailTxn.id!} refreshKey={detailTxn.postingStatus ?? null} />
                        </>
                    )}
                </DialogContent>
                <DialogActions><Button data-help="accounting.transactionList.approval.close" onClick={() => setDetailTxn(null)}>Close</Button></DialogActions>
            </Dialog>
            {/* ---- end FW-3B ---- */}
        </div>
    );
};

export default TransactionList;
